// ===== Packages =====

import React, {
    useState,
    useMemo,
    useEffect,
}                                       from 'react';
import { ReactSVG }                     from 'react-svg';
import moment                           from 'moment-timezone';
import {
    useMatch,
    useNavigate,
    useLocation,
    useSearchParams,
}                                       from 'react-router-dom';

// ===== Components =====

import { Transition }                   from '../Transition';
import {
    Button,
}                                       from '../Editor/helpers';

// ===== Hooks =====

import {
    useLatest,
}                                       from '../../hooks';

// ===== Services =====

import {
    detectTouchDevice,
    markNotificationAsRead,
    recordUserAction,
    getPressEventHandlers,
    getScrollAwareTapPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
    getNotificationGroupItemTypeText,
    getNotificationLinkPath,
}                                       from '../../services';

// ===== Interfaces =====

import {
    INotificationItem,
    INotificationGroupItem,
    IPartialUserItem,
}                                       from '../../interfaces';

// ===== Enums =====

import {
    NOTIFICATION_TYPE,
    NOTIFICATION_GROUP_TYPE,
    BUTTON_TYPE,
    PAGE_ROUTE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    USER_ACTION_TYPE,
}                                       from '../../enums';

// ===== Images =====

import CrossIcon                        from '../../images/cross.svg';
import CommentIcon                      from '../../images/comment.svg';
import SmileyIcon                       from '../../images/editor/smiley.svg';
import AccordionTriangleIcon            from '../../images/accordion-triangle.svg';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    BODY_FONT_SIZE,
    MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
    USER_PROFILE_AVATAR_LENGTH_MULTIPLIER,
    FADE_IN_STAGGER_OFFSET_DURATION,
    FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_TRANSITION_DURATION,
    FADE_IN_STAGGER_DEFAULT_STYLE,
    FADE_IN_STAGGER_TRANSITION_STYLES,
    FADE_IN_OUT_DEFAULT_STYLE,
    FADE_IN_OUT_TRANSITION_STYLES,
    SUPPORT_EMAIL_ADDRESS,
    BOOK_NAME,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    NotificationCollector,
    NotificationTypeIconContainer,
    NotificationTitleContainer,
    NotificationImageContainer,
    NotificationContentContainer,
    NotificationText,
    NotificationBold,
    NotificationItalic,
    NotificationTimestamp,
    ClearAllButtonContainer,
    NotificationExpandContainer,
    NotificationContainer,
    RemoveNotificationButtonContainer,
    RemoveNotificationButton,
    ImageContainer,
    ContentContainer,
}                                       from './styles';
import {
    UserAvatar,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

interface Props {
    customCursorActive: boolean,
    user: IPartialUserItem | null,
    currentSessionId: string | null,
    group: INotificationGroupItem,
    notificationAuthors: Map<string, IPartialUserItem>,
    notificationAvatars: Map<string, string>,
    dismissNotification: (notif: INotificationItem) => Promise<void>,
    setRefreshNotifications: React.Dispatch<React.SetStateAction<boolean>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function NotificationGroup({
    customCursorActive,
    currentSessionId,
    group,
    notificationAuthors,
    notificationAvatars,
    dismissNotification,
    setRefreshNotifications,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element {
    // ===== Refs =====

    const CLEAR_ALL_BUTTON_HEIGHT_MULTIPLIER = 1.5625; // em

    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();
    const annotationIsFocused = useMatch(`${PAGE_ROUTE.book}/:annotationId`);
    const [searchParams] = useSearchParams();

    // ===== State =====

    // stores notifications
    const [notifications, setNotifications] = useState<INotificationItem[]>(group.items);
    // indicates whether group is expanded
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    // stores the height of expanded container
    const [expandedContainerHeight, setExpandedContainerHeight] = useState<number>(0);
    // indicates whether group is being cleared
    const [isClearingGroup, setIsClearingGroup] = useState<boolean>(false);

    // ===== Animation Constants =====

    // ===== Methods =====

    const onNotificationGroupMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.notificationGroup,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onNotificationGroupMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onClearNotificationsButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.clearNotificationsButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onClearNotificationsButtonMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.notificationGroup,
            [CURSOR_SIGN.click],
        );
    };

    const onRemoveNotificationButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.removeNotificationButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onRemoveNotificationButtonMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
        onCursorEnter(
            CURSOR_TARGET.notification,
            [CURSOR_SIGN.click],
        );
    };

    const onNotificationItemMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.notification,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onNotificationItemMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const computeExpandedContainerHeight = (): void => {
        let updatedExpandedContainerHeight = 0;
        notifications.forEach((notif) => {
            const notifNode = document.getElementById(notif.id);
            if (notifNode) {
                updatedExpandedContainerHeight += notifNode.offsetHeight;
            }
        });
        if (expandedContainerHeight !== updatedExpandedContainerHeight) {
            setExpandedContainerHeight(updatedExpandedContainerHeight);
        }
    };

    const toggleExpand = (): void => {
        setIsExpanded(!isExpanded);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.toggleNotificationGroup,
                sessionId: currentSessionId,
            });
        }
    };

    const removeNotification = async (
        notif: INotificationItem,
        e: React.MouseEvent | React.TouchEvent,
    ): Promise<void> => {
        // Stop propagation so group doesn't toggle
        e.stopPropagation();
        // Remove locally
        const updatedNotifications = notifications.filter((n) => n.id !== notif.id);
        setNotifications(updatedNotifications);
        // Remove on server
        dismissNotification(notif);
    };

    const clearAllGroupNotificationsLatest = useLatest({
        currentSessionId,
    });
    const clearAllGroupNotifications = (e: React.MouseEvent | React.TouchEvent): void => {
        // Stop propagation so group doesn't toggle
        e.stopPropagation();
        // Make sure cursor doesn't have odd behavior when button immediately disappears
        onClearNotificationsButtonMouseLeave(e);
        setIsClearingGroup(true);
        const dismissNotificationPromises: Promise<boolean>[] = [];
        for (let i = 0; i < notifications.length; i += 1) {
            const notif = notifications[i];
            dismissNotificationPromises.push(markNotificationAsRead({
                test: isTestMode(),
                notificationId: notif.id,
            }));
        }

        Promise.all(dismissNotificationPromises).then(() => {
            const cur = clearAllGroupNotificationsLatest.current;
            setRefreshNotifications(true);
            // We don't run setIsClearingGroup(false); because we don't
            // want the 'Clear' button to show again before notification refresh
            // But we assume it will happen and this notification group will be removed
            if (cur.currentSessionId) {
                // Record user action
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.clearAllNotificationGroup,
                    sessionId: cur.currentSessionId,
                });
            }
        });
    };

    const getLinkPath = (notif: INotificationItem): string => {
        try {
            return getNotificationLinkPath({
                notification: notif,
                supportEmailAddress: SUPPORT_EMAIL_ADDRESS,
            });
        } catch (error) {
            if (currentSessionId) {
                recordUserAction({
                    test: isTestMode(),
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: `${error}`,
                    },
                });
            }
            throw error;
        }
    };

    const handleNotificationClick = async (notif: INotificationItem, linkPath: string): Promise<void> => {
        dismissNotification(notif);
        navigate(
            linkPath,
            {
                state: {
                    prevPath: location.pathname,
                    chapterId: notif.payload?.chapterId,
                    sectionId: notif.payload?.sectionId,
                },
            },
        );
    };

    // ===== Side Effects =====

    useEffect(() => {
        setNotifications(group.items);
    }, [group.items]);

    useEffect(() => {
        computeExpandedContainerHeight();
    }, [notifications]);

    // ===== Memoization =====

    const groupTypeText = useMemo(() => {
        switch (group.type) {
        case NOTIFICATION_GROUP_TYPE.annotation:
            return 'An annotation you\'re following in ';
        case NOTIFICATION_GROUP_TYPE.general:
        default:
            return 'You ';
        }
    }, [
        group,
    ]);

    const groupTypeVerb = useMemo(() => {
        switch (group.type) {
        case NOTIFICATION_GROUP_TYPE.annotation:
            return ' has ';
        default:
            return 'have ';
        }
    }, [
        group,
    ]);

    const groupItemTypeText = useMemo(() => getNotificationGroupItemTypeText(group.itemType), [
        group,
    ]);

    // ===== Rendering =====

    const renderNotification = (notif: INotificationItem): React.ReactElement => (
        <>
            <RemoveNotificationButtonContainer>
                <RemoveNotificationButton
                    show
                    customCursorActive={customCursorActive}
                    isTouchDevice={detectTouchDevice(document)}
                    className={HOVER_TARGET_CLASSNAME}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e: React.MouseEvent) => onRemoveNotificationButtonMouseEnter(e),
                        onMouseLeave: (e: React.MouseEvent) => onRemoveNotificationButtonMouseLeave(e),
                    })}
                    {...getPressEventHandlers({ onMouseDown: (e) => removeNotification(notif, e) })}
                >
                    <ReactSVG
                        src={CrossIcon}
                    />
                </RemoveNotificationButton>
            </RemoveNotificationButtonContainer>
            <ImageContainer>
                {!!notificationAvatars.get(notif.userId)
                || (
                    notificationAuthors.get(notif.userId)
                    && !!notificationAuthors.get(notif.userId)!.photoURL
                )   ? (
                        <UserAvatar
                            url={
                                notificationAvatars.get(notif.userId)
                                || notificationAuthors.get(notif.userId)!.photoURL!
                            } // Has to be present if notificationAvatar is false
                            length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                        />
                    ) : (
                        <ReactSVG
                            src={SmileyIcon}
                        />
                    )}
            </ImageContainer>
            <ContentContainer>
                <NotificationText>
                    <NotificationBold>
                        {notif.title}
                    </NotificationBold>
                    {'\n'}
                    {notif.type === NOTIFICATION_TYPE.annotationComment && (
                        `"${notif.message}"`
                    )}
                    {notif.type === NOTIFICATION_TYPE.bookInviteClaimed && (() => {
                        const recipientName = `${notif.payload?.recipientName || ''}`.trim();
                        const resolvedBookName = `${notif.payload?.bookName || BOOK_NAME}`.trim() || BOOK_NAME;
                        if (recipientName.length === 0) return 'A book invite was claimed.';
                        return (
                            <>
                                {'Good news! '}
                                {recipientName}
                                {' accepted your invite to read '}
                                <NotificationItalic>
                                    {resolvedBookName}
                                </NotificationItalic>
                                .
                            </>
                        );
                    })()}
                    {notif.type !== NOTIFICATION_TYPE.annotationComment
                        && notif.type !== NOTIFICATION_TYPE.bookInviteClaimed
                        && notif.message}
                    {'\n'}
                    <NotificationTimestamp>
                        {`${moment.utc(notif.timestamp).fromNow()}`}
                    </NotificationTimestamp>
                </NotificationText>
            </ContentContainer>
        </>
    );

    return (
        <Container>
            <NotificationCollector
                className={HOVER_TARGET_CLASSNAME}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e: React.MouseEvent) => onNotificationGroupMouseEnter(e),
                    onMouseLeave: (e: React.MouseEvent) => onNotificationGroupMouseLeave(e),
                })}
                {...getScrollAwareTapPressEventHandlers({ onTap: toggleExpand })}
                showClearButton={isExpanded}
            >
                <NotificationTypeIconContainer
                    small
                    rotate={isExpanded}
                >
                    <ReactSVG
                        src={AccordionTriangleIcon}
                    />
                </NotificationTypeIconContainer>
                <NotificationImageContainer>
                    <ReactSVG
                        src={CommentIcon}
                    />
                </NotificationImageContainer>
                <NotificationContentContainer>
                    <NotificationTitleContainer>
                        <NotificationText>
                            {groupTypeText}
                            {group.groupName && (
                                <NotificationBold>
                                    {group.groupName}
                                </NotificationBold>
                            )}
                            {groupTypeVerb}
                            <NotificationBold>
                                {notifications.length}
                            </NotificationBold>
                            {` new ${groupItemTypeText}${notifications.length > 1 ? 's' : ''}. `}
                        </NotificationText>
                    </NotificationTitleContainer>
                </NotificationContentContainer>
                <ClearAllButtonContainer
                    isClearingGroup={isClearingGroup}
                    isTouchDevice={detectTouchDevice(document)}
                >
                    <Button
                        type={BUTTON_TYPE.solid}
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        background={theme.verascopeColor.purple400}
                        height={CLEAR_ALL_BUTTON_HEIGHT_MULTIPLIER * BODY_FONT_SIZE}
                        text="Clear"
                        loading={isClearingGroup}
                        disabled={isClearingGroup}
                        {...(isExpanded
                            ? { onMouseDown: clearAllGroupNotifications }
                            : {}
                        )}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e: React.MouseEvent) => onClearNotificationsButtonMouseEnter(e),
                            onMouseLeave: (e: React.MouseEvent) => onClearNotificationsButtonMouseLeave(e),
                        })}
                    />
                </ClearAllButtonContainer>
            </NotificationCollector>
            <NotificationExpandContainer
                expand={isExpanded}
                expandContainerHeight={expandedContainerHeight}
            >
                {notifications
                    .sort((a, b) => new Date(a.timestamp).getTime()
                        - new Date(b.timestamp).getTime())
                    .map((notif, i) => {
                        const linkPath = getLinkPath(notif);
                        const numNotifs = notifications.length;
                        return (
                            <Transition
                                key={notif.id}
                                in={isExpanded && !notif.read}
                                timeout={!notif.read
                                    ? {
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (i * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((numNotifs - Math.max(i - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    } : {
                                        enter: FADE_IN_TRANSITION_DURATION,
                                        exit: FADE_IN_TRANSITION_DURATION,
                                    }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {
                                    (state) => (
                                        <NotificationContainer
                                            id={notif.id}
                                            key={notif.id}
                                            className={HOVER_TARGET_CLASSNAME}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: (e: React.MouseEvent) => onNotificationItemMouseEnter(e),
                                                onMouseLeave: (e: React.MouseEvent) => onNotificationItemMouseLeave(e),
                                            })}
                                            {...getPressEventHandlers({ onMouseDown: () => handleNotificationClick(notif, linkPath) })}
                                            {...(!annotationIsFocused
                                                && notif.type === NOTIFICATION_TYPE.annotationComment
                                                ? {}
                                                : {
                                                    style: !notif.read
                                                        ? {
                                                            ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                                direction: 'right',
                                                                offset: 50,
                                                            }),
                                                            ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                                direction: 'right',
                                                                offset: 50,
                                                                numItems: numNotifs,
                                                                index: i,
                                                            })[state],
                                                        } : {
                                                            ...FADE_IN_OUT_DEFAULT_STYLE({
                                                                direction: 'right',
                                                            }),
                                                            ...FADE_IN_OUT_TRANSITION_STYLES({
                                                                direction: 'right',
                                                            })[state],
                                                        },
                                                }
                                            )}
                                        >
                                            {renderNotification(notif)}
                                        </NotificationContainer>
                                    )
                                }
                            </Transition>
                        );
                    })}
            </NotificationExpandContainer>
        </Container>
    );
}

export default NotificationGroup;
